/** AI Import — launch form. Crawl a tour operator's website with Claude and
 *  stage itineraries/tours/activities as reviewable drafts (nothing is
 *  written until the per-record review is approved).
 *
 *  Entry flavors via query params: ?type=itinerary|tour|activity|catalog
 *  and optional &to=<tenantId> to pre-seed the tenant picker.
 */
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import type { Accept } from 'react-dropzone';
import { useNavigate, useSearchParams } from 'react-router-dom';

import * as aiImportApi from '@/api/aiImport';
import type { AiImportMode, AiImportUploadFile } from '@/api/aiImport';
import { FieldHelp, labelWithHelp } from '@/components/FieldHelp';
import { UploadDropzone } from '@/components/ImageGallery/UploadDropzone';
import { useSnackbar } from '@/components/Snackbar';
import { HelpLink } from '@/components/HelpLink';
import { TenantPicker } from '@/components/TenantPicker/TenantPicker';
import { pageTenantsFrom, singlePageTenant } from '@/scope/pageTenants';
import { useScopedTenantState } from '@/scope/useScopedTenantState';

import { AI_IMPORT_HELP } from './fieldHelp';
import {
  RestechSourcePanel,
  listableAccounts,
  type RestechSourceValue,
} from './RestechSourcePanel';

const MODES: { value: AiImportMode; label: string }[] = [
  { value: 'itinerary', label: 'Itineraries' },
  { value: 'tour', label: 'Tours' },
  { value: 'activity', label: 'Activities' },
  { value: 'catalog', label: 'Whole catalog' },
];

const DOC_ACCEPT: Accept = {
  'application/pdf': ['.pdf'],
  'text/plain': ['.txt', '.text', '.md'],
  'application/rtf': ['.rtf'],
  'text/rtf': ['.rtf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
};

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function parseUrls(raw: string): string[] {
  return raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

function validateUrls(urls: string[], cap: number): string | null {
  if (urls.length === 0) return 'At least one URL is required.';
  if (urls.length > cap) return `At most ${cap} URLs for this mode.`;
  for (const url of urls) {
    let parsed: URL;
    try {
      parsed = new URL(url);
    } catch {
      return `Not a valid URL: ${url}`;
    }
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return `Only http(s) URLs are supported: ${url}`;
    }
  }
  return null;
}

export function AiImportPage() {
  const { show } = useSnackbar();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const initialMode = (searchParams.get('type') ?? 'catalog') as AiImportMode;
  // `?to=<id>` (the "New from URL (AI)" buttons carry their page's tenant) seeds
  // the picker as a deliberate choice: shown, and it beats the console scope.
  const presetTenant = singlePageTenant(pageTenantsFrom(searchParams.get('to')));

  const [tenant, setTenant] = useScopedTenantState(presetTenant);
  const [mode, setMode] = useState<AiImportMode>(
    MODES.some((m) => m.value === initialMode) ? initialMode : 'catalog',
  );
  const [urlsRaw, setUrlsRaw] = useState('');
  const [model, setModel] = useState('');
  const [maxPages, setMaxPages] = useState('');
  const [maxDepth, setMaxDepth] = useState('');
  const [maxWorkers, setMaxWorkers] = useState('');
  const [budget, setBudget] = useState('');
  const [dryRun, setDryRun] = useState(false);
  const [restechSource, setRestechSource] = useState<RestechSourceValue>({
    choice: null,
    integration: '',
    accounts: [],
  });
  const [staged, setStaged] = useState<AiImportUploadFile[]>([]);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [enhancePlaces, setEnhancePlaces] = useState(true);

  const tenantId = tenant?.id ?? null;

  const ctxQuery = useQuery({
    queryKey: ['ai-import', 'launch-context', tenantId ?? 'none'],
    queryFn: () => aiImportApi.launchContext(tenantId ?? undefined),
    staleTime: 5 * 60_000,
  });
  const ctx = ctxQuery.data;

  const urls = useMemo(() => parseUrls(urlsRaw), [urlsRaw]);
  const urlCap = mode === 'catalog' ? (ctx?.caps.max_seed_urls ?? 5) : (ctx?.caps.max_urls ?? 50);
  const urlError = urlsRaw.trim() ? validateUrls(urls, urlCap) : null;

  const placesAvailable = Boolean(ctx?.google_places.available);
  const maxFiles = ctx?.uploads.max_files ?? 10;

  // Catalog crawl depth: blank = unlimited; otherwise a whole number >= 0.
  const maxDepthError =
    mode === 'catalog' && maxDepth.trim() !== '' && !/^\d+$/.test(maxDepth.trim())
      ? 'Whole number ≥ 0, or blank for unlimited.'
      : null;
  const maxWorkersError =
    maxWorkers.trim() !== '' && !/^[1-9]\d*$/.test(maxWorkers.trim())
      ? 'Whole number ≥ 1, or blank for 1.'
      : null;

  const handleFiles = useCallback(
    async (fileList: File[]) => {
      const remaining = maxFiles - staged.length - uploadingCount;
      const toUpload = fileList.slice(0, Math.max(0, remaining));
      if (toUpload.length < fileList.length) {
        show(`At most ${maxFiles} documents.`, 'warning');
      }
      if (toUpload.length === 0) return;
      setUploadingCount((c) => c + toUpload.length);
      await Promise.all(
        toUpload.map(async (file) => {
          try {
            const up = await aiImportApi.upload(file);
            setStaged((prev) => [...prev, up]);
          } catch (e) {
            show(`${file.name}: ${(e as Error).message}`, 'error');
          } finally {
            setUploadingCount((c) => c - 1);
          }
        }),
      );
    },
    [maxFiles, staged.length, uploadingCount, show],
  );

  const removeStaged = (key: string) =>
    setStaged((prev) => prev.filter((f) => f.file_key !== key));

  const selectedModel = useMemo(() => {
    if (!ctx) return null;
    return ctx.models.find((m) => (model ? m.id === model : m.default)) ?? null;
  }, [ctx, model]);

  // The restech choice only matters when the run can produce tours and the
  // tenant has a product-listing booking integration.
  const restechRelevant =
    (mode === 'tour' || mode === 'catalog') &&
    Boolean(ctx?.restech?.has_booking_integration) &&
    listableAccounts(ctx?.restech ?? null).size > 0;
  const restechPrimary = restechRelevant && restechSource.choice === 'restech';

  const startMut = useMutation({
    mutationFn: () =>
      aiImportApi.start({
        tour_operator_id: tenantId!,
        mode,
        urls,
        model: model || null,
        max_pages: mode === 'catalog' && maxPages ? Number(maxPages) : undefined,
        max_depth:
          mode === 'catalog' && maxDepth.trim() !== '' ? Number(maxDepth) : undefined,
        max_workers: maxWorkers.trim() !== '' ? Number(maxWorkers) : undefined,
        budget_usd: budget ? Number(budget) : undefined,
        dry_run: dryRun,
        documents: staged.map((f) => ({
          file_key: f.file_key,
          filename: f.filename,
          content_type: f.content_type,
        })),
        skip_google_places: placesAvailable ? !enhancePlaces : true,
        ...(restechPrimary
          ? {
              restech_primary: true,
              restech_integration:
                restechSource.integration || [...listableAccounts(ctx?.restech ?? null).keys()][0],
              restech_accounts: restechSource.accounts,
            }
          : {}),
      }),
    onSuccess: (r) => {
      show('AI import started — extraction is running.', 'success');
      navigate(`/bgtasks/${r.id}`);
    },
    onError: (e) => show((e as Error).message, 'error'),
  });

  const restechReady =
    !restechRelevant ||
    restechSource.choice === 'web' ||
    (restechSource.choice === 'restech' && restechSource.accounts.length > 0);
  const hasInput = urls.length > 0 || staged.length > 0;
  const canStart =
    Boolean(tenantId) &&
    hasInput &&
    !urlError &&
    !maxDepthError &&
    !maxWorkersError &&
    restechReady &&
    uploadingCount === 0 &&
    !startMut.isPending;

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <AutoAwesomeIcon color="primary" />
        <Typography variant="h5">AI Import</Typography>
        <HelpLink story="ai-import" />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Point Claude at a tour operator&apos;s website and/or upload brochures (PDF, DOCX, RTF,
        TXT). Content is extracted into draft records — you review every record with a checkbox
        before anything is imported.
      </Typography>

      <Stack spacing={2.5}>
        <TenantPicker label="Tenant" value={tenant} onChange={setTenant} />

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            What to create <FieldHelp help={AI_IMPORT_HELP.mode} />
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={mode}
            onChange={(_, v: AiImportMode | null) => v && setMode(v)}
          >
            {MODES.map((m) => (
              <ToggleButton key={m.value} value={m.value}>
                {m.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        <TextField
          label={
            mode === 'catalog' ? 'Seed URLs (site roots, one per line)' : 'Page URLs (one per line)'
          }
          value={urlsRaw}
          onChange={(e) => setUrlsRaw(e.target.value)}
          multiline
          minRows={3}
          error={Boolean(urlError)}
          helperText={
            urlError ??
            'Optional if you upload documents below. Each URL may yield multiple records.'
          }
          InputProps={{ endAdornment: <FieldHelp help={AI_IMPORT_HELP.urls} /> }}
        />

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Documents (PDF, DOCX, RTF, TXT) — optional, can be mixed with URLs{' '}
            <FieldHelp help={AI_IMPORT_HELP.documents} />
          </Typography>
          <UploadDropzone
            multiple
            accept={DOC_ACCEPT}
            label="Drag brochures or documents here"
            browseLabel="Add documents"
            onFiles={handleFiles}
            disabled={staged.length >= maxFiles}
          />
          {(staged.length > 0 || uploadingCount > 0) && (
            <Stack direction="row" sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
              {staged.map((f) => (
                <Chip
                  key={f.file_key}
                  label={`${f.filename} · ${formatBytes(f.size)}`}
                  onDelete={() => removeStaged(f.file_key)}
                  variant="outlined"
                />
              ))}
              {uploadingCount > 0 && (
                <Chip
                  icon={<CircularProgress size={14} />}
                  label={`Uploading ${uploadingCount}…`}
                  variant="outlined"
                />
              )}
            </Stack>
          )}
        </Box>

        <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TextField
              select
              label="Extraction model"
              value={model || (ctx?.models.find((m) => m.default)?.id ?? '')}
              onChange={(e) => setModel(e.target.value)}
              sx={{ minWidth: 260 }}
              disabled={!ctx}
              helperText={
                selectedModel
                  ? `$${selectedModel.input_cost_per_mtok}/M in · $${selectedModel.output_cost_per_mtok}/M out`
                  : undefined
              }
            >
              {(ctx?.models ?? []).map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.label}
                  {m.default ? ' (default)' : ''}
                </MenuItem>
              ))}
            </TextField>
            <FieldHelp help={AI_IMPORT_HELP.model} />
          </Box>

          <TextField
            label="Budget cap (USD)"
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
            placeholder={ctx ? String(ctx.caps.max_cost_usd_default) : ''}
            helperText={ctx ? `Max $${ctx.caps.max_cost_usd_cap}` : undefined}
            sx={{ width: 160 }}
            InputProps={{ endAdornment: <FieldHelp help={AI_IMPORT_HELP.budget} /> }}
          />

          <TextField
            label="Parallel workers"
            value={maxWorkers}
            onChange={(e) => setMaxWorkers(e.target.value)}
            placeholder="1"
            error={Boolean(maxWorkersError)}
            helperText={
              maxWorkersError ??
              (ctx ? `Faster crawl · up to ${ctx.caps.max_workers_cap}` : '1 = sequential')
            }
            sx={{ width: 170 }}
            InputProps={{ endAdornment: <FieldHelp help={AI_IMPORT_HELP.workers} /> }}
          />

          {mode === 'catalog' && (
            <TextField
              label="Max pages"
              value={maxPages}
              onChange={(e) => setMaxPages(e.target.value)}
              placeholder={ctx ? String(ctx.caps.max_pages_default) : ''}
              helperText={ctx ? `Max ${ctx.caps.max_pages_cap}` : undefined}
              sx={{ width: 140 }}
              InputProps={{ endAdornment: <FieldHelp help={AI_IMPORT_HELP.maxPages} /> }}
            />
          )}

          {mode === 'catalog' && (
            <TextField
              label="Max depth"
              value={maxDepth}
              onChange={(e) => setMaxDepth(e.target.value)}
              placeholder="Unlimited"
              error={Boolean(maxDepthError)}
              helperText={maxDepthError ?? '0 = seed URLs only · blank = no limit'}
              sx={{ width: 170 }}
              InputProps={{ endAdornment: <FieldHelp help={AI_IMPORT_HELP.maxDepth} /> }}
            />
          )}
        </Stack>

        {restechRelevant && ctx?.restech && (
          <RestechSourcePanel
            restech={ctx.restech}
            value={restechSource}
            onChange={setRestechSource}
          />
        )}

        <FormControlLabel
          control={<Checkbox checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />}
          label={labelWithHelp(
            'Dry run — extract and review, but skip the final import',
            AI_IMPORT_HELP.dryRun,
          )}
        />

        {placesAvailable && (
          <FormControlLabel
            control={
              <Checkbox
                checked={enhancePlaces}
                onChange={(e) => setEnhancePlaces(e.target.checked)}
              />
            }
            label={labelWithHelp(
              'Enhance activities with Google Places (you confirm every match)',
              AI_IMPORT_HELP.googlePlaces,
            )}
          />
        )}

        {mode === 'catalog' && (
          <Alert severity="info">
            Catalog mode walks the site from the seed URLs (same domain, robots.txt respected).
            Crawling stops at the page cap or when 80% of the budget is spent — partial results
            still reach the review step.
          </Alert>
        )}

        <Box>
          <Button
            variant="contained"
            size="large"
            startIcon={startMut.isPending ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
            disabled={!canStart}
            onClick={() => startMut.mutate()}
          >
            Start extraction
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
